// Auto-discovery helpers used by the topbar org/workspace pickers.
//
// These hit the Libertify Auth API (mounted at `/api/...`), not the Media
// API (mounted at `/media/...`).

import type { Env } from '@/types';
import { getEndpoints } from './endpoints';

export interface Organization {
  id: string;
  name?: string;
  /** Free-form passthrough — the backend may add more fields in the future. */
  [k: string]: unknown;
}

export interface Workspace {
  id: string;
  name?: string;
  organization_id?: string;
  [k: string]: unknown;
}

export class DiscoveryError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'DiscoveryError';
    this.status = status;
  }
}

async function authedJson<T>(env: Env, path: string, accessToken: string): Promise<T> {
  const { apiBase } = getEndpoints(env);
  const resp = await fetch(`${apiBase}${path}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!resp.ok) {
    let detail = '';
    try {
      const j = (await resp.clone().json()) as { detail?: string | { message?: string; code?: string } };
      if (typeof j.detail === 'string') detail = j.detail;
      else if (j.detail && typeof j.detail === 'object') detail = j.detail.message || j.detail.code || '';
    } catch {
      const text = await resp.text().catch(() => '');
      detail = text.slice(0, 200);
    }
    if (resp.status === 401) {
      throw new DiscoveryError('Your session expired — please sign in again.', 401);
    }
    throw new DiscoveryError(detail || `GET ${path} failed with HTTP ${resp.status}`, resp.status);
  }
  return (await resp.json()) as T;
}

export async function listOrganizations(env: Env, accessToken: string): Promise<Organization[]> {
  // The auth proxy returns either a bare array or a wrapped object — handle both.
  const data = await authedJson<unknown>(env, '/api/users/me/organizations', accessToken);
  return normalizeArray<Organization>(data);
}

export async function listWorkspaces(
  env: Env,
  accessToken: string,
  orgId: string
): Promise<Workspace[]> {
  const data = await authedJson<unknown>(env, `/api/${encodeURIComponent(orgId)}/workspaces`, accessToken);
  return normalizeArray<Workspace>(data);
}

function normalizeArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const key of ['items', 'data', 'results', 'organizations', 'workspaces']) {
      const v = obj[key];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}
